import { formatCount, formatPercent } from '@/lib/format';
import type { MainShareBasis } from '@/layout/specs';
import type { DisplayNodes, FunnelResult } from '@/model/types';

interface Props {
  result: FunnelResult;
  display: DisplayNodes;
  shareBasis: MainShareBasis;
}

interface ConversionRow {
  id: string;
  label: string;
  path: string;
  rate: number;
  populationValue: number;
  numerator: number;
  previousDenominator: number;
  annotation: string;
}

export function ConversionMetricsTable({ result, display, shareBasis }: Props) {
  const count = formatCount;
  const rows: ConversionRow[] = [
    {
      id: 'screening-reach',
      label: 'Screening reach',
      path: 'Total population → Engaged (screening)',
      rate: result.rates.screeningReach,
      populationValue: result.nodes.reachedScreening,
      numerator: display.reachedScreening,
      previousDenominator: display.population,
      annotation:
        `${count(display.reachedScreening)} eligible people were reached for screening across the source populations; ` +
        `${count(display.neverReached)} were not reached. This is the size-weighted blend of the source reach rates.`,
    },
    {
      id: 'screening-completion',
      label: 'Screening completion',
      path: 'Engaged (screening) → Screened',
      rate: result.rates.screenGivenReached,
      populationValue: result.nodes.screened,
      numerator: display.screened,
      previousDenominator: display.reachedScreening,
      annotation:
        `Of those reached, ${count(display.screened)} completed at least a PHQ-2; ` +
        `${count(display.notScreened)} were reached but not screened. This is the reached-weighted blend of the source screening rates.`,
    },
    {
      id: 'followup-required',
      label: 'Follow-up required',
      path: 'Screened → Follow-up required',
      rate: result.rates.followupRequiredShare,
      populationValue: result.nodes.followupRequired,
      numerator: display.followupRequired,
      previousDenominator: display.screened,
      annotation:
        `${count(display.followupRequired)} screened people scored PHQ-9 10 or higher and entered the follow-up pool; ` +
        `${count(display.noFollowupNeeded)} did not require follow-up. ${count(display.cssrsNeeded)} also require C-SSRS—` +
        'overlapping clinical work, not a separate branch, so they remain in the same pool.',
    },
    {
      id: 'followup-reach',
      label: 'Follow-up reach',
      path: 'Follow-up required → Engaged (follow-up)',
      rate: result.rates.followupReach,
      populationValue: result.nodes.reachedFollowup,
      numerator: display.reachedFollowup,
      previousDenominator: display.followupRequired,
      annotation:
        `${count(display.reachedFollowup)} people in the follow-up pool were reached again; ` +
        `${count(display.notReachedFollowup)} were never reached again. This is the pool-weighted blend of the cohort reach rates.`,
    },
    {
      id: 'followup-completion',
      label: 'Follow-up completion',
      path: 'Engaged (follow-up) → Follow-up completed',
      rate: result.rates.followupCompletion,
      populationValue: result.nodes.completed,
      numerator: display.completed,
      previousDenominator: display.reachedFollowup,
      annotation:
        `Of those reached for follow-up, ${count(display.completed)} completed a qualifying encounter; ` +
        `${count(display.notCompleted)} were reached but did not complete. Structured coding and the 30-day timing gate sit downstream and are not drawn.`,
    },
  ];
  const populationBase = result.nodes.population;
  const rateFor = (row: ConversionRow) =>
    shareBasis === 'population'
      ? populationBase > 0
        ? row.populationValue / populationBase
        : 0
      : row.rate;
  const denominatorFor = (row: ConversionRow) =>
    shareBasis === 'population' ? display.population : row.previousDenominator;
  const basisLabel = shareBasis === 'population' ? '% of population' : '% of previous';

  return (
    <section className="conversion-section" aria-labelledby="conversion-metrics-title">
      <h2 id="conversion-metrics-title">Conversion metrics</h2>
      <p id="conversion-metrics-note" className="conversion-note">
        {shareBasis === 'population'
          ? 'Each rate and denominator uses the outreach population after exclusions.'
          : 'Each rate and denominator uses the preceding step.'}{' '}
        Counts are rounded to whole people and reconcile to the diagram.
      </p>

      <div className="conversion-table-wrap">
        <table className="conversion-table" aria-describedby="conversion-metrics-note">
          <caption>Stage-by-stage conversion metrics</caption>
          <colgroup>
            <col className="conversion-col-metric" />
            <col className="conversion-col-rate" />
            <col className="conversion-col-volume" />
            <col className="conversion-col-annotation" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Conversion</th>
              <th scope="col">Rate ({basisLabel})</th>
              <th scope="col">Numerator / denominator</th>
              <th scope="col">What’s happening</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope="row">
                  <span className="conversion-label">{row.label}</span>
                  <span className="conversion-path">{row.path}</span>
                </th>
                <td className="conversion-rate">{formatPercent(rateFor(row))}</td>
                <td className="conversion-volume">
                  <strong>{count(row.numerator)}</strong>
                  <span> / {count(denominatorFor(row))}</span>
                </td>
                <td className="conversion-annotation">{row.annotation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
